/** @jsxImportSource @emotion/react */
import { useEffect, useState } from 'react'
import { Button } from 'react-bootstrap'
import ReactSelect from 'react-select'
import { toast } from 'react-toastify'

import type { OfficeSettingsDto } from 'ts-back-end/src/business/office/dto/office.dto'
import { useGetOfficeSettingsQuery, useUpdateOfficeSettingsMutation } from 'store/services/office'
import { useGetLabPayerListQuery } from 'store/services/payer'

interface IProps {
  officeId: number
}

// Mirrors OfficeBillingType on the back end — declared here rather than
// imported, like every other enum the lab UI uses.
type OfficeBillingType = 'CLIENT_BILL_ONLY' | 'MIXED'

const billingTypeOptions: { value: OfficeBillingType; label: string }[] = [
  { value: 'CLIENT_BILL_ONLY', label: 'Client Bill Only' },
  { value: 'MIXED', label: 'Mixed (Insurance + Client Bill)' },
]

const billingTypeHint: Record<OfficeBillingType, string> = {
  CLIENT_BILL_ONLY:
    'Every new order gets this payer as its primary, the insurance panel is locked, and the order is' +
    ' marked billed automatically.',
  MIXED:
    'Insurance is entered as usual; every new order is flagged for a biller to confirm Client Bill vs' +
    ' Insurance (the Client Bill Review list).',
}

type Draft = {
  billingType: OfficeBillingType | null
  clientBillPayerId: number | null
}

const toDraft = (settings?: OfficeSettingsDto): Draft => ({
  billingType: (settings?.billingType as OfficeBillingType | null | undefined) ?? null,
  clientBillPayerId: settings?.clientBillPayerId ?? null,
})

/**
 * The office's billing designation and the client-bill payer that goes with
 * it. Edited as a draft and saved explicitly: the pair is one decision, and
 * the server refuses a designation without a Client Bill payer — so a
 * half-made choice never gets written on the way to the second field.
 */
export const OfficeBillingTypeSection = ({ officeId }: IProps) => {
  const { data: officeSettings } = useGetOfficeSettingsQuery({ id: officeId })
  const [updateOfficeSettings, { isLoading: isSaving }] = useUpdateOfficeSettingsMutation()
  // Only payers classified Client Bill qualify — that is what the type is for.
  const { data: clientBillPayers, isLoading: isLoadingPayers } = useGetLabPayerListQuery({
    payerType: 'CLIENT_BILL',
    active: true,
    size: 100,
    page: 0,
  })

  const [draft, setDraft] = useState<Draft>(toDraft(officeSettings))

  useEffect(() => {
    setDraft(toDraft(officeSettings))
  }, [officeSettings])

  const saved = toDraft(officeSettings)
  const dirty = draft.billingType !== saved.billingType || draft.clientBillPayerId !== saved.clientBillPayerId
  const payerMissing = Boolean(draft.billingType) && !draft.clientBillPayerId
  const canSave = dirty && !payerMissing && !isSaving

  const payerOptions: { value: number; label: string }[] = (clientBillPayers?.items ?? []).map(
    (payer: { id: number; name?: string }) => ({
      value: payer.id,
      label: payer.name || `Payer #${payer.id}`,
    }),
  )

  const save = async () => {
    try {
      await updateOfficeSettings({
        id: officeId,
        // The local union mirrors the back-end enum value for value.
        billingType: draft.billingType as OfficeSettingsDto['billingType'],
        clientBillPayerId: draft.billingType ? draft.clientBillPayerId : null,
      }).unwrap()
      toast.success(draft.billingType ? 'Office billing type saved' : 'Office billing type cleared')
    } catch (error) {
      const message = (error as { data?: { message?: string } })?.data?.message
      toast.error(message || 'Failed to save the office billing type')
    }
  }

  return (
    <div>
      <div className="form-group textGray">
        <label htmlFor="officeBillingType">Billing Type</label>
        <ReactSelect
          inputId="officeBillingType"
          isClearable
          isDisabled={isSaving}
          menuPortalTarget={document.body}
          placeholder="Not set — orders bill as usual"
          options={billingTypeOptions}
          value={billingTypeOptions.find((option) => option.value === draft.billingType) ?? null}
          onChange={(selected) =>
            setDraft((current) => ({ ...current, billingType: selected?.value ?? null }))
          }
          styles={{ menuPortal: (base) => ({ ...base, zIndex: 10000 }) }}
        />
        {draft.billingType && (
          <div className="text-muted mt-1" css={{ fontSize: '0.8rem' }}>
            {billingTypeHint[draft.billingType]}
          </div>
        )}
      </div>
      {draft.billingType && (
        <div className="form-group textGray">
          <label htmlFor="officeClientBillPayer">
            Client Bill Payer <span className="text-danger">*</span>
          </label>
          <ReactSelect
            inputId="officeClientBillPayer"
            isDisabled={isSaving}
            isLoading={isLoadingPayers}
            menuPortalTarget={document.body}
            placeholder={
              payerOptions.length ? 'Pick the client bill payer…' : 'No payers of type Client Bill yet'
            }
            noOptionsMessage={() => 'No payers of type Client Bill — set the type on a payer first'}
            options={payerOptions}
            value={payerOptions.find((option) => option.value === draft.clientBillPayerId) ?? null}
            onChange={(selected) =>
              setDraft((current) => ({ ...current, clientBillPayerId: selected?.value ?? null }))
            }
            styles={{
              menuPortal: (base) => ({ ...base, zIndex: 10000 }),
              control: (base) => (payerMissing ? { ...base, borderColor: '#dc3545' } : base),
            }}
          />
          {payerMissing && (
            <div className="text-danger mt-1" css={{ fontSize: '0.8rem' }}>
              A client bill payer is required for this billing type.
            </div>
          )}
        </div>
      )}
      <div className="d-flex justify-content-end">
        {dirty && (
          <Button
            variant="outline-secondary"
            className="mr-2"
            disabled={isSaving}
            onClick={() => setDraft(saved)}>
            Undo
          </Button>
        )}
        <Button variant="primary" disabled={!canSave} onClick={save}>
          {isSaving ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </div>
  )
}
